import * as actionTypes from './actionTypes'

/**
 * Creates a connect action that takes a credentials object.
 *
 * @method connect
 * @param {Object} credentials The credentials to pass to the connect action.
 * @param {Object} [options] Any parameters that aren't credentials.
 * @return {Object} A flux standard action.
 */
export function connect (credentials, options) {
  return {
    type: actionTypes.CONNECT,
    payload: { credentials, options },
    meta: {
      isSensitive: true
    }
  }
}

/**
 * Create a set connection info action that takes a connection and user info object
 *
 * @method setConnectionInfo
 * @param {Object} $0
 * @param {Object} $0.userInfo An object representing the user information.
 * @param {Object} $0.connection A connection object. Information about how to connect to the backend services.
 * @return {Object} A flux standard action.
 */
export function setConnectionInfo ({ userInfo, connection }, platform) {
  return {
    type: actionTypes.SET_CONNECTION_INFO,
    payload: { userInfo, connection },
    meta: {
      platform,
      isSensitive: true
    }
  }
}

/**
 * Connection occurred action.
 * Signifies that a connection has been made to a service, but that the connection
 *      workflow has not finished yet. Intended for use in scenarios where the
 *      workflow connects to multiple services, to represent the "intermediate"
 *      connections.
 *
 * @method connectionOccurred
 * @param  {Object} $0
 * @param  {Object} $0.subscription
 * @param  {Object} $0.connection
 * @param  {Object} [$0.error] An error message. Only present if an error occurred.
 * @param  {string} platform The backend platform used for the connection.
 * @return {Object} A flux standard action.
 */
export function connectionOccurred ({ subscription, connection, error }, platform) {
  // TODO: Is this action used anywhere?
  var action = {
    type: actionTypes.CONNECTION_OCCURRED,
    meta: { platform }
  }

  if (error) {
    action.error = true
    action.payload = error
  } else {
    action.payload = { subscription, connection }
  }

  return action
}

/**
 * Create a connect finished action that takes a userInfo object on success and possibly
 * an error object.
 *
 * @method connectFinished
 * @param {Object} $0
 * @param {Object} $0.subscription A subscription object. Contains what services to subscribe to.
 * @param {Object} $0.userInfo An object representing the user information.
 * @param {Object} $0.connection A connection object. Information about how to connect to the backend services.
 * @param {string} [$0.error] An error message. Only present if an error occurred.
 * @param {string} platform The backend platform we are currently on.
 * @param {boolean} isSSO Boolean for whether the current connection scenario is SSO or not.
 * @return {Object} A flux standard action.
 */
export function connectFinished ({ subscription, userInfo, connection, error }, platform, isSSO = false) {
  var action = {
    type: actionTypes.CONNECT_FINISHED,
    meta: {
      platform,
      isSSO,
      isSensitive: true
    }
  }

  if (error) {
    action.error = true
    action.payload = error
  } else {
    action.payload = { subscription, userInfo, connection }
  }

  return action
}

/**
 * Creates a getUserDetails action
 *
 * @method getUserDetails
 * @return {Object} A flux standard action.
 */
export function getUserDetails () {
  return { type: actionTypes.GET_USER_DETAILS }
}

/**
 * Create a user details received action
 *
 * @method userDetailsReceived
 * @param  {Object} userDetailsResponse An object representing the REST response of a user details request.
 * @return {Object} A flux standard action.
 */
export function userDetailsReceived (userDetailsResponse) {
  var action = {
    type: actionTypes.USER_DETAILS_RECEIVED,
    payload: {
      firstName: userDetailsResponse.firstName || userDetailsResponse.user_first_name,
      user_first_name: userDetailsResponse.user_first_name || userDetailsResponse.firstName,
      lastName: userDetailsResponse.lastName || userDetailsResponse.user_last_name,
      user_last_name: userDetailsResponse.user_last_name || userDetailsResponse.lastName,
      photoURL: userDetailsResponse.photoURL,
      emailAddress: userDetailsResponse.emailAddress || userDetailsResponse.user_email,
      user_email: userDetailsResponse.user_email || userDetailsResponse.emailAddress,
      username: userDetailsResponse.username
    }
  }

  return action
}

/**
 * Creates a disconnect action.
 *
 * @method disconnect
 * @return {Object} A flux standard action.
 */
export function disconnect () {
  return { type: actionTypes.DISCONNECT }
}

/**
 * Create a disconnectFinished action that possibly takes an error object on failure.
 *
 * @method disconnectFinished
 * @param {Object} params
 * @param {string} [params.error] An error message. Only present if an error occurred.
 * @param {string} [params.reason] Why the disconnectFinished action is being dispatched.
 * @param {number} [params.retryAfter] The time (in seconds) when SDK should retry the operation.
 * @return {Object} A flux standard action.
 */
export function disconnectFinished ({ error, reason, retryAfter } = {}) {
  const action = {
    type: actionTypes.DISCONNECT_FINISHED,
    payload: {}
  }

  if (error) {
    action.error = true
    action.payload = error
  }

  if (reason) {
    action.payload.reason = reason
  }

  if (retryAfter) {
    action.payload.retryAfter = retryAfter
  }
  return action
}

/**
 * Action creator representing the finish of a resubscription request.
 * Payload mirrors a connect finished action.
 *
 * @method resubscribeFinished
 * @param {Object} $0
 * @param {string} [$0.error] An error message. Only present if an error occurred.
 * @param {string} [$0.attemptNum] The attempt number of this resubscription.
 * @param {string} platform The backend platform we are currently on.
 * @return {Object} A flux standard action.
 */
export function resubscribeFinished ({ error, attemptNum }, platform) {
  var action = {
    type: actionTypes.RESUBSCRIPTION_FINISHED,
    meta: { platform }
  }

  if (error) {
    action.error = true
    action.payload = error
    action.payload.attemptNum = attemptNum
  } else {
    action.payload = {
      attemptNum
    }
  }

  return action
}

/**
 * Creates a refreshTokens action with the given credentials as a payload.
 *
 * @method refreshTokens
 * @param {Object} credentials A credentials object containing tokens.
 * @return {Object} A flux standard action.
 */
export function refreshTokens (credentials) {
  var action = {
    type: actionTypes.REFRESH_TOKENS,
    payload: { credentials }
  }
  return action
}

/**
 * Creates a refreshTokensFinished action with connection and platform information.
 * Optionally includes an error.
 *
 * @method refreshTokensFinished
 * @param {Object} $0
 * @param {Object} [$0.error] An optional error object.
 * @param {Object} $0.connection Connection information.
 * @param {string} platform The backend platform we are currently on.
 * @return {Object} A flux standard action.
 */
export function refreshTokensFinished ({ error, connection }, platform) {
  var action = {
    type: actionTypes.REFRESH_TOKENS_FINISHED,
    payload: { connection },
    meta: { platform }
  }
  if (error) {
    action.error = true
    action.payload = error
  }

  return action
}

/**
 * Represents a request to update a current subscription.
 * @method updateSubscription
 * @param  {Object} subscription Information used to update subscription.
 * @return {Object} A flux standard action.
 */
export function updateSubscription (subscription) {
  return {
    type: actionTypes.UPDATE_SUBSCRIPTION,
    payload: subscription
  }
}

/**
 * Represents that the current subscription has been updated.
 * @method updateSubscriptionFinished
 * @param  {Object} $0
 * @param  {Object} $0.subscription New subscription state to be updated.
 * @param  {Object} [$0.error] Error object, in the case of an error.
 * @param  {string} platform The backend platform used for the subscription.
 * @return {Object} A flux standard action.
 */
export function updateSubscriptionFinished ({ subscription, error }, platform) {
  var action = {
    type: actionTypes.UPDATE_SUBSCRIPTION_FINISH,
    meta: { platform }
  }

  if (error) {
    action.error = true
    action.payload = error
  } else {
    action.payload = subscription
  }
  return action
}

/**
 * Sets the token information in the state.
 * @method setTokens
 * @param {Object} params Dictionary object of parameters.
 * @param {string} accessToken An access token retrieved using the authentication APIs of the platform.
 * @param {string} idToken An identity token retrieved using the authentication APIs of the platform.
 * @return {Object} A flux standard action.
 */
export function setTokens ({ accessToken, idToken }) {
  return {
    type: actionTypes.SET_TOKEN,
    payload: { accessToken, idToken }
  }
}

/**
 * Creates a setCredentials action that takes a credentials object.
 *
 * @method setCredentials
 * @param {Object} $0
 * @param {string} $0.username The username.
 * @param {string} $0.password The user's password.
 * @param {string} $0.authname The user's authorization name.
 * @param {string} $0.hmacToken An HMAC token for the user with the provided user ID.
 * @param {string} $0.bearerAccessToken An access token retrieved using the authentication APIs of the platform.
 * @param {string} $0.idToken An identity token retrieved using the authentication APIs of the platform.
 * @param {string} $0.accessToken An access token retrieved using the authentication APIs of the platform. Used for CIM authentication.
 * @return {Object} A flux standard action.
 */
export function setCredentials ({ username, password, authname, hmacToken, bearerAccessToken, idToken, accessToken }) {
  return {
    type: actionTypes.SET_CREDENTIALS,
    payload: { username, password, authname, hmacToken, bearerAccessToken, idToken, accessToken },
    meta: {
      isSensitive: true
    }
  }
}

/**
 * Create a setCredentials finished action that takes a userInfo object on success and possibly
 * an error object.
 *
 * @method setCredentialsFinished
 * @param {Object} $0
 * @param {Object} $0.userInfo An object representing the user information.
 * @param {Object} $0.connection A connection object. Information about how to connect to the backend services.
 * @param {string} [$0.error] An error message. Only present if an error occurred.
 * @param {string} platform The backend platform we are currently on.
 * @return {Object} A flux standard action.
 */
export function setCredentialsFinished ({ userInfo, connection, error }, platform) {
  var action = {
    type: actionTypes.SET_CREDENTIALS_FINISH,
    meta: {
      platform,
      isSensitive: true
    }
  }

  if (error) {
    action.error = true
    action.payload = error
  } else {
    action.payload = { userInfo, connection }
  }

  return action
}

/**
 * Creates an updateToken action with connection and platform information.
 *
 * @method updateToken
 * @param {string} token A session id retrieved after a successful subscription on the platform.
 * @param {string} platform The backend platform we are currently on.
 * @return {Object} A flux standard action.
 */
export function updateHmacToken (token, platform) {
  var action = {
    type: actionTypes.UPDATE_HMAC_TOKEN,
    payload: token,
    meta: {
      platform,
      isSensitive: true
    }
  }

  return action
}
